import type { GeneratedDesign } from '@mg/shared';
import { api } from '../../lib/api';

/** Gap between status reads. Short enough to feel live, long enough not to be a busy-wait. */
const POLL_INTERVAL_MS = 2_000;
/**
 * The client's own ceiling on waiting.
 *
 * The server already ends jobs two ways — a dead worker is declared failed after its row goes quiet,
 * and an async provider gets a four-minute budget — so this is not the real deadline. It exists so the
 * modal can never spin for ever against a server that answers "running" indefinitely, which is a
 * failure mode a client should survive on its own rather than trust away.
 */
const CLIENT_CEILING_MS = 6 * 60_000;

/**
 * Watch a design job until it produces a design, or fail loudly.
 *
 * Resolves with the same {@link GeneratedDesign} the inline path returns, which is the point: the
 * caller gets one success shape and does not care which route produced it. Progress arrives through
 * `onStep` as the server's own sentence — written for a person and shown verbatim, rather than a
 * percentage this cannot honestly compute.
 */
export async function awaitDesignJob(
  jobId: string,
  onStep: (step: string) => void,
  signal?: AbortSignal,
): Promise<GeneratedDesign> {
  const startedAt = Date.now();
  for (;;) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (Date.now() - startedAt > CLIENT_CEILING_MS) {
      throw new Error(
        'This design is taking longer than expected. It may still finish and appear in your Media Library.',
      );
    }
    await sleep(POLL_INTERVAL_MS, signal);

    const state = await api.designJobState(jobId, signal);
    if (state.status === 'ready' && state.design) return state.design;
    if (state.status === 'failed') {
      throw new Error(state.error ?? 'The design could not be generated.');
    }
    onStep(state.step);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
